import { encodeString, decodeString } from 'root/utils/string/encoding';

const includedFields = [
    'company',
    'full_name',
    'phone',
    'email',
    'role',
    'business_type',
    'time_to_purchase',
    'existing_business',
    'feature_interest',
    'inventory_volume',
    'resto_business_type',
    'secondary_business_type',
    'general_opt_in',
    'existing_business',
    'user_set_country_code',
    'country',
    'gtv',
    'zip',
    'postal_code',
];
const leadformPrefix = 'leadform';

// Returns true if the form field name is part of the the `includedFields`
const isIncludedField = (elName: string) => {
    if (elName) {
        return includedFields.includes(elName);
    }

    return false;
};

// Returns true if the form name is part of the `excludedForms` array
const isFormExcludedFromLocalStorage = (form: HTMLElement) => {
    if (form && form.dataset.formName) {
        const excludedForms = [
            'other_reseller',
            'other_email_preference_center',
            'other_partner_application',
        ];

        return excludedForms.includes(form.dataset.formName);
    }

    return false;
};

/*
 * Checks if the current element is a select tag,
 * if yes, it returns true when the current selected option has the `data-adp` attribute
 * */
const isADPBusinessType = (el: HTMLElement | HTMLScriptElement) => {
    return el instanceof HTMLSelectElement
        ? el.options[el.selectedIndex].hasAttribute('data-adp')
        : false;
};

// Returns true if field has a value and is a phone field or not hidden
const isValidFormEl = (el: HTMLInputElement | HTMLSelectElement, elName: string) => {
    return el.value !== '' && (elName === 'phone' || el.type !== 'hidden');
};

// Saves valid form field values to the local storage
function storeEncodedFormFieldValue(
    el: HTMLInputElement | HTMLSelectElement,
    elName: string
) {
    if (isIncludedField(elName) && !isADPBusinessType(el) && isValidFormEl(el, elName)) {
        saveEncodedValueToLocalStorage(elName, el.value);
    }
}

// Saves encoded value to localStorage
function saveEncodedValueToLocalStorage(
    key: string,
    value: string,
    prefix: string = leadformPrefix
) {
    (<any>window).registerCookieComplianceCallback({
        callback: () =>
            window.localStorage.setItem(`${prefix}.${key}`, encodeString(value)),
        category: 'functional',
        isCallOptanonWrapper: true,
    });
}

// Returns true if the data-form-autofill attribute is set to "enabled"
function isEnabledFormAutofill() {
    return document.body.dataset.formAutofill === 'enabled';
}

function removeKeyFromLocalStorage(key: string, prefix: string = leadformPrefix) {
    window.localStorage.removeItem(`${prefix}.${key}`);
}

// Deletes local storage when the isEnabledFormAutofill() is set to false
function clearLocalStorageBasedOnBodyAttr(prefix: string = leadformPrefix) {
    if (!isEnabledFormAutofill()) {
        includedFields.forEach(includedField => {
            window.localStorage.removeItem(`${prefix}.${includedField}`);
        });
        window.localStorage.removeItem('ls.barcode.email');
    }
}

// Removes key:value pair from localStorage
function removeFormFieldValue(key: string) {
    if (navigator.cookieEnabled) {
        window.localStorage.removeItem(`${leadformPrefix}.${key}`);
    }
}

// Gets decoded local storage item
function getDecodedFormFieldValue(fieldName: string) {
    return isIncludedField(fieldName) ? getDecodedValueFromLocalStorage(fieldName) : null;
}

// Gets value from localStorage and decodes it
function getDecodedValueFromLocalStorage(key: string, prefix: string = leadformPrefix) {
    const localStorageValue =
        window && navigator.cookieEnabled
            ? window.localStorage.getItem(`${prefix}.${key}`)
            : null;
    return localStorageValue ? decodeString(localStorageValue) : null;
}

export {
    storeEncodedFormFieldValue,
    getDecodedFormFieldValue,
    removeFormFieldValue,
    isFormExcludedFromLocalStorage,
    clearLocalStorageBasedOnBodyAttr,
    isEnabledFormAutofill,
    getDecodedValueFromLocalStorage,
    saveEncodedValueToLocalStorage,
    removeKeyFromLocalStorage,
};
